import classNames from 'classnames'
import { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'react-feather'

export const NewCarousel = forwardRef(
  (
    {
      autoPlay = false,
      interval = 3000,
      items = [],
      showIndicators = true,
      showButtons = true,
      cbOnChange = () => {},
      indicatorsContainerClass = '',
      dotExtraPadding = false,
      idPrefix,
      peakSlider = false,
      trackClassName = 'gap-5',
    },
    ref
  ) => {
    const [currentIndex, setCurrentIndex] = useState(1)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [enableTransition, setEnableTransition] = useState(true)
    const carouselRef = useRef(null)
    const touchStartX = useRef(0)
    const isDragging = useRef(false)
    const intervalRef = useRef(null)

    const extendedSlides = [
      items[items.length - 1], // Clone of last slide
      ...items, // Real slides
      items[0], // Clone of first slide
    ]

    // Restart autoplay timer on user interaction
    const restartAutoplay = () => {
      if (autoPlay && items.length > 1) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
        intervalRef.current = setInterval(() => {
          setCurrentIndex((prevIndex) => {
            const nextIndex = prevIndex + 1

            // If we're going past the last slide, trigger the infinite loop
            if (nextIndex > items.length) {
              // We'll be at the cloned first slide, need to reset after animation
              setTimeout(() => {
                setEnableTransition(false)
                setCurrentIndex(1)
                setTimeout(() => {
                  setEnableTransition(true)
                }, 50)
              }, 500)
              return extendedSlides.length - 1 // Go to cloned first slide
            }

            return nextIndex
          })
        }, interval)
      }
    }

    //Trigger cb on sliderChange
    useEffect(() => {
      if (currentIndex > 0 && currentIndex <= items.length && typeof cbOnChange === 'function') {
        if (currentIndex == 1 && !enableTransition) {
          cbOnChange(currentIndex)
        } else {
          setTimeout(() => cbOnChange(currentIndex), 500)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex])

    // Auto-play functionality
    useEffect(() => {
      if (!autoPlay || items.length <= 1) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
        return
      }

      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          const nextIndex = prevIndex + 1

          // If we're going past the last slide, trigger the infinite loop
          if (nextIndex > items.length) {
            // We'll be at the cloned first slide, need to reset after animation
            setTimeout(() => {
              setEnableTransition(false)
              setCurrentIndex(1)
              setTimeout(() => {
                setEnableTransition(true)
              }, 50)
            }, 500)
            return extendedSlides.length - 1 // Go to cloned first slide
          }

          return nextIndex
        })
      }, interval)

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
      }
    }, [autoPlay, interval, items.length, extendedSlides.length])

    const goToNext = () => {
      if (isTransitioning) return
      restartAutoplay() // Restart autoplay on manual interaction

      if (currentIndex === items.length) {
        setIsTransitioning(true)
        // Step 1: Animate to the cloned slide
        setCurrentIndex(extendedSlides.length - 1)
        // Step 2: After animation completes, instantly jump to real slide
        setTimeout(() => {
          setEnableTransition(false) // Disable transition
          setCurrentIndex(1) // Jump to real slide 1
          // Re-enable transitions after jump
          setTimeout(() => {
            setEnableTransition(true)
            setIsTransitioning(false)
          }, 50)
        }, 500)
      } else {
        // Normal next slide
        setIsTransitioning(true)
        setCurrentIndex(currentIndex + 1)
        setTimeout(() => {
          setIsTransitioning(false)
        }, 500)
      }
    }

    const goToPrevious = () => {
      if (isTransitioning) return

      restartAutoplay() // Restart autoplay on manual interaction

      if (currentIndex === 1) {
        setIsTransitioning(true)

        setCurrentIndex(0)
        setTimeout(() => {
          setEnableTransition(false) // Disable transition
          setCurrentIndex(items.length) // Jump to real slide
          // Re-enable transitions after jump
          setTimeout(() => {
            setEnableTransition(true)
            setIsTransitioning(false)
          }, 50)
        }, 500)
      } else {
        // Normal previous slide
        setIsTransitioning(true)
        setCurrentIndex(currentIndex - 1)
        setTimeout(() => {
          setIsTransitioning(false)
        }, 500)
      }
    }

    // Touch/Mouse events for horizontal scrolling
    const handleTouchStart = (e) => {
      if (isTransitioning) return
      touchStartX.current = e.touches ? e.touches[0].clientX : e.clientX
      isDragging.current = true
      restartAutoplay() // Restart autoplay on touch
    }

    const handleTouchMove = (e) => {
      if (!isDragging.current || isTransitioning) return
      // Prevent default to avoid page scrolling
      e.preventDefault()
    }

    const handleTouchEnd = (e) => {
      if (!isDragging.current || isTransitioning) return

      const touchEndX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX
      const deltaX = touchStartX.current - touchEndX
      const threshold = 50 // Minimum swipe distance

      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0) {
          // Swiped left -> go to next slide
          goToNext()
        } else {
          // Swiped right -> go to previous slide
          goToPrevious()
        }
      }

      isDragging.current = false
    }

    const handleMouseDown = (e) => {
      handleTouchStart(e)
      e.preventDefault()
    }

    const handleMouseMove = (e) => {
      handleTouchMove(e)
    }

    const handleMouseUp = (e) => {
      handleTouchEnd(e)
    }

    const handleMouseLeave = () => {
      isDragging.current = false
    }

    // Wheel scrolling (horizontal)
    const handleWheel = (e) => {
      if (isTransitioning) return

      e.preventDefault()
      const deltaX = e.deltaX || e.deltaY // Use deltaY if deltaX not available
      const threshold = 30

      restartAutoplay() // Restart autoplay on wheel

      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0) {
          goToNext()
        } else {
          goToPrevious()
        }
      }
    }

    // Get current actual slide index for indicators
    const getCurrentSlideIndex = () => {
      if (currentIndex === 0) return items.length - 1 // Cloned last slide
      if (currentIndex === extendedSlides.length - 1) return 0 // Cloned first slide
      return currentIndex - 1 // Real slides (offset by 1)
    }

    // Go to specific slide from indicator click
    const goToSlide = (targetIndex) => {
      if (targetIndex < 0 || targetIndex >= items.length || isTransitioning) return

      restartAutoplay()
      setIsTransitioning(true)
      setCurrentIndex(targetIndex + 1) // +1 because real slides start at index 1

      setTimeout(() => {
        setIsTransitioning(false)
      }, 500)
    }

    // Expose methods
    useImperativeHandle(ref, () => ({
      goNext: goToNext,
      goPrevious: goToPrevious,
      goToSlide: goToSlide,
    }))

    if (!items || items.length === 0) {
      return <></>
    }

    return (
      <div className="relative w-full lg:overflow-hidden" data-testid={`${idPrefix}-container`}>
        {/* Viewport mask */}
        <div
          ref={carouselRef}
          className="relative overflow-hidden select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onWheel={handleWheel}
        >
          {/* Track */}
          <div
            className={`flex h-full ${trackClassName} min-w-0 ${
              enableTransition ? 'transition-transform duration-500 ease-in-out delay-200' : ''
            }`}
            style={{
              transform: `translateX(calc(-${currentIndex * 90}% - ${
                currentIndex * 20
              }px + 2% + 10px))`,
            }}
          >
            {extendedSlides.map((slide, index) => (
              <div
                key={`slide-${index}`}
                className={
                  peakSlider
                    ? `shrink-0 ${
                        currentIndex == items.length && peakSlider ? 'mr-3' : ''
                      } w-[88%] sm:w-[90%] lg:w-full h-full`
                    : 'shrink-0 w-[90%] h-full'
                }
              >
                {slide}
              </div>
            ))}
          </div>

          {showButtons && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute top-1/2 left-4 -translate-y-1/2 bg-white hover:bg-blue-100 text-[#30f] p-2 rounded-lg border-none z-10"
              >
                <ChevronLeft />
              </button>

              <button
                onClick={goToNext}
                className="absolute top-1/2 right-4 -translate-y-1/2 bg-white hover:bg-blue-100 text-[#30f] p-2 rounded-lg border-none z-10"
              >
                <ChevronRight />
              </button>
            </>
          )}
        </div>

        {showIndicators && items.length > 1 && (
          <div className={dotExtraPadding ? 'pt-[20px] mb-[10px]' : ''}>
            <div
              className={classNames(
                'absolute bottom-4 left-1/2 -translate-x-1/2 flex  z-10 py-[2px] rounded-md bg-[#5C708A4D] *:first:ml-[4px]! *:last:mr-[4px]! p-x-[1px]',
                indicatorsContainerClass
              )}
            >
              {items.map((_, index) => (
                <button
                  key={`indicator-${index}`}
                  aria-label={`Go to ${index + 1} item`}
                  onClick={() => goToSlide(index)}
                  disabled={isTransitioning}
                  className={`w-[6px] h-[6px] p-0 ml-1 rounded-lg border-none cursor-pointer transition-all duration-200 ${
                    index === getCurrentSlideIndex() ? 'bg-white' : 'bg-[#2C47F6]'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }
)

NewCarousel.displayName = 'NewCarousel'

export default NewCarousel
